import React from "react";
import { X, Plus } from "lucide-react";
import { useWorkspace, WorkspaceTab } from "./workspace-context";
import { useSidebar } from "@/components/ui/sidebar";

/**
 * Must match your sidebar width exactly.
 * This controls how far right the tabs shift
 * when the sidebar is open.
 */
const SIDEBAR_WIDTH = 280;

function tabLabel(tab: WorkspaceTab): string {
  // Simply use the tab title which is already properly formatted by Workspace
  return tab.title;
}

export default function BottomTabBar() {
  const {
    state,
    openTab,
    closeTab,
    setActiveTab,
    setTabMinimized,
    isTabMinimized,
  } = useWorkspace();

  // ShadCN sidebar state — true when expanded
  const { open: sidebarOpen } = useSidebar();

  const tabs = state.tabs;
  const activeId = state.activeTabId;

  const handleTabClick = (tab: WorkspaceTab) => {
    if (isTabMinimized(tab.id) || tab.isMinimized) {
      setTabMinimized(tab.id, false);
    }
    setActiveTab(tab.id);
  };

  const handleClose = (tab: WorkspaceTab) => closeTab(tab.id);
  const handleAddChat = () => openTab("chat", { forceNew: true });

  return (
    <div
      className="
        fixed inset-x-0 bottom-0
        flex h-10 items-center
        border-t border-white/10
        bg-[rgba(20,10,40,0.95)]
        backdrop-blur-xl
        z-[60]
      "
      style={{
        zIndex: 50,
      }}
      data-tutorial="bottom_bar"
    >

      {/* ========= PURPLE CHROME HUD GLOW LINES (BOTTOM) ========= */}

      {/* Thin sharp neon line */}
      <div
        className="
          pointer-events-none
          absolute inset-x-0 top-0
          h-[1px]
          bg-gradient-to-r
          from-transparent via-purple-300/80 to-transparent
          opacity-80
        "
      />

      {/* ========================================================== */}

      <div className="flex h-full w-full items-center gap-1 overflow-x-auto overflow-y-hidden px-2">

        {/* + button */}
        <button
          onClick={handleAddChat}
          className="
            flex h-6 w-6 shrink-0 items-center justify-center
            rounded-md border border-white/10
            bg-[rgba(12,12,20,0.95)]
            hover:bg-[rgba(28,14,45,0.95)]
            transition-colors mr-2
          "
          title="New tab"
        >
          <Plus className="h-3 w-3 text-neutral-200" />
        </button>

        {/* Tabs */}
        <div className="flex flex-1 items-center gap-1 overflow-x-auto overflow-y-hidden">
          {tabs.map((tab) => {
            const isActive = tab.id === activeId;
            const label = tabLabel(tab);

            return (
              <div
                key={tab.id}
                className={`group relative flex shrink-0 items-center gap-1 text-sm transition-all duration-200 ${
                  isActive
                    ? `
                      border border-purple-300/80
                      bg-gradient-to-b from-purple-600 via-purple-700 to-purple-800
                      text-white
                    `
                    : `
                      border border-white/10
                      bg-[rgba(18,18,30,0.95)]
                      text-neutral-300
                      hover:bg-[rgba(32,32,50,0.95)]
                      hover:text-white
                    `
                }`}
                style={{
                  height: 30,
                  borderRadius: 8,
                  paddingLeft: 6,
                  paddingRight: 14,
                }}
              >

                <button
                  type="button"
                  className="
                    rounded p-0.5
                    text-neutral-300 opacity-0
                    transition-opacity
                    group-hover:opacity-100
                    hover:text-white hover:bg-white/10
                  "
                  onClick={() => handleClose(tab)}
                  aria-label={`Close ${label}`}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <X className="h-3 w-3" />
                </button>

                <button
                  type="button"
                  onClick={() => handleTabClick(tab)}
                  className="flex items-center gap-2 text-xs font-medium"
                  title={label}
                >
                  <span className="truncate max-w-[160px]">{label}</span>
                  {tab.dirty && (
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                      aria-hidden
                    />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
